/**
 * @file: A stable colour for each playlist.
 *
 * Colour used to be computed from a priority's position between the lowest and
 * highest in the fetched window. So raising one interval's priority recoloured
 * the whole week, the same playlist differed between two adjacent weeks, and the
 * interval that reached the screen came out red - the colour that should mean
 * something is wrong.
 *
 * Colour now says which playlist it is, and nothing else. Whether an interval
 * reaches the screen is said by the on-screen strip and by a marker on the bar.
 */

/**
 * These are the colours of the playlists in the calendar.
 *
 * The app chose these colours for the calendar. They are not a published
 * palette. Each colour has these properties:
 * - Bar text in readableOn() has a contrast of 4.5 to 1 or more.
 * - The contrast against the light grid and the dark grid is 3 to 1 or more.
 * - The colour is far from the danger red, the primary blue and the warning
 *   amber, because those colours have a meaning of their own.
 *
 * The lightness is in two near steps, so no colour is much stronger than the
 * others. The order puts different hues next to each other, because the first
 * playlists of a scene take the first entries.
 *
 * The media labels and the scenes keep the palette of ColorService.
 */
const PLAYLIST_COLOURS = Object.freeze([
  '#a87dda', // Lavender
  '#9b932b', // Olive
  '#2ca28a', // Jade
  '#af5187', // Plum
  '#487f38', // Leaf
  '#bd8369', // Clay
  '#2692aa', // Lake
  '#9b670e' // Ochre
])

/**
 * Give the colour of a playlist in the calendar of its scene.
 *
 * The rank of the playlist id among the ids of the scene selects the colour.
 * Two playlists on one scene therefore get two colours, up to the length of
 * the list. A colour from the id alone repeated in 22 % of scenes with three
 * playlists.
 *
 * The ids are the full set of the scene and not the playlists of one week. A
 * playlist thus keeps its colour from week to week. The colour changes only
 * when a playlist with a lower id leaves the scene.
 *
 * @param {number|string} sceneCollectionId - The playlist
 * @param {Iterable} scenePlaylistIds - The ids of every playlist on the scene
 * @returns {string} A hex colour from PLAYLIST_COLOURS
 */
function colourForPlaylist (sceneCollectionId, scenePlaylistIds) {
  const id = Number(sceneCollectionId)

  if (!Number.isFinite(id)) {
    return PLAYLIST_COLOURS[0]
  }

  const lower = new Set(Array.from(scenePlaylistIds || [], Number).filter(other => other < id))

  return PLAYLIST_COLOURS[lower.size % PLAYLIST_COLOURS.length]
}

/** Convert one sRGB channel to its linear value. */
function channel (eightBit) {
  const unit = eightBit / 255
  return unit <= 0.03928 ? unit / 12.92 : Math.pow((unit + 0.055) / 1.055, 2.4)
}

/** Give the WCAG relative luminance of a #rgb or #rrggbb colour. */
function luminance (hex) {
  const value = String(hex || '').replace('#', '')
  const full = value.length === 3
    ? value.split('').map(digit => digit + digit).join('')
    : value

  if (full.length !== 6) {
    return 0
  }

  const red = channel(parseInt(full.slice(0, 2), 16))
  const green = channel(parseInt(full.slice(2, 4), 16))
  const blue = channel(parseInt(full.slice(4, 6), 16))

  return 0.2126 * red + 0.7152 * green + 0.0722 * blue
}

/**
 * Black or white, whichever contrasts more with the fill. The palette holds
 * colours on both sides of the middle, so one fixed text colour fails on some.
 *
 * @returns {string} '#000000' or '#ffffff'
 */
function readableOn (hex) {
  const relative = luminance(hex)
  const onWhite = 1.05 / (relative + 0.05)
  const onBlack = (relative + 0.05) / 0.05

  return onBlack >= onWhite ? '#000000' : '#ffffff'
}

/** The stripes of a hatch use the ink of the bar text at this opacity. */
const HATCH_STRENGTH = 0.32

/**
 * Give the stripe colour of a hatch over a fill.
 *
 * A white stripe was 1.04 to 1 on the lightest playlist colour, so a covered bar
 * looked like a plain bar. The ink of the bar text contrasts with every fill,
 * so a stripe in that ink shows on every fill.
 *
 * @returns {string} An rgba() colour
 */
function hatchOn (hex) {
  const ink = readableOn(hex) === '#000000' ? '0, 0, 0' : '255, 255, 255'

  return `rgba(${ink}, ${HATCH_STRENGTH})`
}

export {
  PLAYLIST_COLOURS,
  colourForPlaylist,
  hatchOn,
  luminance,
  readableOn
}
